import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Clear as CancelIcon,
  Mic as MicIcon,
  Replay as RetryIcon,
  Stop as StopIcon,
} from '@mui/icons-material';

import { transcribeAgentAudio } from '../../services/api';

export const MAX_DICTATION_BYTES = 10 * 1024 * 1024;
export const DICTATION_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/webm',
];

export const preferredDictationMimeType = (Recorder = window.MediaRecorder) => {
  if (!Recorder || typeof Recorder.isTypeSupported !== 'function') return null;
  return DICTATION_MIME_TYPES.find((mediaType) => Recorder.isTypeSupported(mediaType)) || null;
};

export const appendDictationTranscript = (draft, transcript) => {
  const existing = String(draft || '').trimEnd();
  const addition = String(transcript || '').trim();
  if (!addition) return existing;
  return `${existing}${existing ? ' ' : ''}${addition}`;
};

const stopStream = (stream) => {
  stream?.getTracks?.().forEach((track) => track.stop());
};

function DictationControl({
  active,
  disabled,
  onBusyChange,
  onTranscript,
  resetToken,
  idleLabel = 'Dictate message',
}) {
  const [mode, setMode] = useState('idle');
  const [feedback, setFeedback] = useState('');
  const [failed, setFailed] = useState(false);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const pendingAudioRef = useRef(null);
  const generationRef = useRef(0);

  const supportedMimeType = useMemo(() => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return null;
    return preferredDictationMimeType();
  }, []);
  const isBusy = mode === 'recording' || mode === 'transcribing' || mode === 'retry';

  useEffect(() => {
    onBusyChange(isBusy);
  }, [isBusy, onBusyChange]);

  const releaseResources = useCallback(() => {
    generationRef.current += 1;
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      if (recorder.state !== 'inactive') recorder.stop();
    }
    recorderRef.current = null;
    stopStream(streamRef.current);
    streamRef.current = null;
    chunksRef.current = [];
    pendingAudioRef.current = null;
  }, []);

  const discardRecording = useCallback(() => {
    releaseResources();
    setMode('idle');
    setFeedback('');
    setFailed(false);
  }, [releaseResources]);

  useEffect(() => {
    if (!active) discardRecording();
  }, [active, discardRecording]);

  useEffect(() => releaseResources, [releaseResources]);

  useEffect(() => {
    discardRecording();
  }, [discardRecording, resetToken]);

  const transcribe = useCallback(async (audio, generation) => {
    setMode('transcribing');
    setFailed(false);
    setFeedback('Creating an accurate draft…');
    try {
      const response = await transcribeAgentAudio(audio);
      if (generationRef.current !== generation) return;
      onTranscript(response.data.transcript);
      pendingAudioRef.current = null;
      setMode('idle');
      setFeedback('Dictation added. Review or edit the draft before sending.');
    } catch (error) {
      if (generationRef.current !== generation) return;
      pendingAudioRef.current = audio;
      setMode('retry');
      setFailed(true);
      setFeedback(
        error.response?.data?.message
          || 'I could not transcribe that recording. Your draft is unchanged.',
      );
    }
  }, [onTranscript]);

  const startRecording = async () => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    pendingAudioRef.current = null;
    setFailed(false);
    setFeedback('Requesting microphone access…');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (generationRef.current !== generation) {
        stopStream(stream);
        return;
      }
      const recorder = new window.MediaRecorder(stream, { mimeType: supportedMimeType });
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        if (generationRef.current !== generation) return;
        stopStream(streamRef.current);
        streamRef.current = null;
        recorderRef.current = null;
        setMode('idle');
        setFailed(true);
        setFeedback('Recording failed. Your existing draft is unchanged.');
      };
      recorder.start();
      setMode('recording');
      setFeedback('Listening continuously. Tap Done when you finish.');
    } catch (error) {
      stopStream(streamRef.current);
      streamRef.current = null;
      recorderRef.current = null;
      setMode('idle');
      setFailed(true);
      setFeedback(
        error?.name === 'NotAllowedError'
          ? 'Microphone access was denied. You can enable it in browser settings; your draft is unchanged.'
          : 'Dictation could not start. Your existing draft is unchanged.',
      );
    }
  };

  const finishRecording = async () => {
    const recorder = recorderRef.current;
    const generation = generationRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    setMode('transcribing');
    setFeedback('Finishing recording…');
    const audio = await new Promise((resolve, reject) => {
      recorder.onerror = () => reject(new Error('The recording could not be finalized.'));
      recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: supportedMimeType }));
      recorder.stop();
    }).catch((error) => {
      if (generationRef.current === generation) {
        setMode('idle');
        setFailed(true);
        setFeedback(`${error.message} Your existing draft is unchanged.`);
      }
      return null;
    });
    stopStream(streamRef.current);
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    if (!audio || generationRef.current !== generation) return;
    if (!audio.size) {
      setMode('idle');
      setFailed(true);
      setFeedback('No audio was captured. Please record it again.');
      return;
    }
    if (audio.size > MAX_DICTATION_BYTES) {
      setMode('idle');
      setFailed(true);
      setFeedback('That dictation is too long. Please record a shorter message.');
      return;
    }
    await transcribe(audio, generation);
  };

  const retryTranscription = async () => {
    const audio = pendingAudioRef.current;
    if (!audio) {
      setMode('idle');
      return;
    }
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    await transcribe(audio, generation);
  };

  const handlePrimaryAction = () => {
    if (mode === 'recording') void finishRecording();
    else if (mode === 'retry') void retryTranscription();
    else if (mode === 'idle') void startRecording();
  };

  const primaryLabel = mode === 'recording'
    ? 'Done recording'
    : mode === 'retry'
      ? 'Retry transcription'
      : idleLabel;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={supportedMimeType ? primaryLabel : 'Dictation is unavailable in this browser'}>
        <span>
          <IconButton
            aria-label={primaryLabel}
            aria-pressed={mode === 'recording'}
            color={mode === 'recording' ? 'error' : 'primary'}
            disabled={disabled || mode === 'transcribing' || !supportedMimeType}
            onClick={handlePrimaryAction}
          >
            {mode === 'transcribing' && <CircularProgress size={22} />}
            {mode === 'recording' && <StopIcon />}
            {mode === 'retry' && <RetryIcon />}
            {mode === 'idle' && <MicIcon />}
          </IconButton>
        </span>
      </Tooltip>
      {(mode === 'recording' || mode === 'retry') && (
        <Tooltip title="Cancel dictation and preserve the current draft">
          <IconButton aria-label="Cancel dictation" size="small" onClick={discardRecording}>
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {feedback && (
        <Typography
          aria-live="polite"
          color={failed ? 'error' : 'text.secondary'}
          variant="caption"
          sx={{ position: 'absolute', left: 16, bottom: 2 }}
        >
          {feedback}
        </Typography>
      )}
    </Box>
  );
}

export default DictationControl;
